export default {
  /**
     * Genereert de border-radius varianten op basis van één 'md'-waarde in rem.
     * @param {string|number} mdValue Bijvoorbeeld "1.6rem" of 1.6
     */
  generateBorderRadius(mdValue) {
    const root = document.documentElement;
    const mdNum =
      typeof mdValue === "string" ? parseFloat(mdValue) : Number(mdValue);

    if (isNaN(mdNum)) {
      console.warn("generateBorderRadius: ongeldig formaat:", mdValue);
      return;
    }

    // Als de maat 0 is, alle varianten op 0 zetten
    if (mdNum === 0) {
      root.style.setProperty("--ui-border-radius-sm", "0rem");
      root.style.setProperty("--ui-border-radius-md", "0rem");
      root.style.setProperty("--ui-border-radius-lg", "0rem");
      root.style.setProperty("--ui-border-radius-xl", "0rem");
      return;
    }

    // Normale berekeningen
    const sm = (mdNum * 0.5).toFixed(2) + "rem"; // 50% van md
    const md = mdNum.toFixed(2) + "rem"; // basis
    const lg = (mdNum * 1.5).toFixed(2) + "rem"; // 150% van md
    const xl = (mdNum * 2.0).toFixed(2) + "rem"; // 200% van md

    root.style.setProperty("--ui-border-radius-sm", sm);
    root.style.setProperty("--ui-border-radius-md", md);
    root.style.setProperty("--ui-border-radius-lg", lg);
    root.style.setProperty("--ui-border-radius-xl", xl);
  },

  generateColorVariants(color) {
    if (!color || typeof color !== "string" || !color.startsWith("#")) {
      console.warn("generateColorVariants: ongeldige kleur:", color);
      return;
    }

    const root = document.documentElement;
    const hsl = this.hexToHsl(color);

    if (hsl) {
      // 1. Genereer kleurvarianten
      for (let i = 1; i <= 9; i++) {
        const lightnessAdjustment = 10 * (5 - i);
        const adjustedLightness = hsl.l + lightnessAdjustment;
        const variantColor = this.hslToHex({
          ...hsl,
          l: Math.min(100, Math.max(0, adjustedLightness))
        });

        root.style.setProperty(`--k-app-primary-${i}00`, variantColor);
      }

      // 2. Zet de gekozen kleur als primary
      root.style.setProperty("--ui-accent-primary", color);

      // 3. Foreground: 90% wit over primary
      const foreground = this.mixWithWhite(color, 90);
      root.style.setProperty("--ui-accent-primary-foreground", foreground);

      // 4. Secondary: 95% wit over primary
      const secondary = this.mixWithWhite(color, 95);
      root.style.setProperty("--ui-accent-secondary", secondary);

      // 5. Secondary foreground = dezelfde als primary
      root.style.setProperty("--ui-accent-secondary-foreground", color);
    }
  },

  mixWithWhite(hex, percent) {
    const amount = percent / 100;

    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    const mixedR = Math.round((1 - amount) * r + amount * 255);
    const mixedG = Math.round((1 - amount) * g + amount * 255);
    const mixedB = Math.round((1 - amount) * b + amount * 255);

    return `#${mixedR.toString(16).padStart(2, "0")}${mixedG
      .toString(16)
      .padStart(2, "0")}${mixedB.toString(16).padStart(2, "0")}`;
  },

  hexToHsl(hex) {
    let r = parseInt(hex.substring(1, 3), 16) / 255;
    let g = parseInt(hex.substring(3, 5), 16) / 255;
    let b = parseInt(hex.substring(5, 7), 16) / 255;

    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let h, s;
    let l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
  },

  hslToHex(hsl) {
    let { h, s, l } = hsl;
    s /= 100;
    l /= 100;

    let c = (1 - Math.abs(2 * l - 1)) * s;
    let x = c * (1 - Math.abs(h / 60 % 2 - 1));
    let m = l - c / 2;

    let r, g, b;
    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];

    return `#${[r, g, b]
      .map(val => Math.round((val + m) * 255).toString(16).padStart(2, "0"))
      .join("")}`;
  },

  hexToRgb(hex) {
    const shorthand = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthand, (m, r, g, b) => r + r + g + g + b + b);
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return match
      ? {
          r: parseInt(match[1], 16),
          g: parseInt(match[2], 16),
          b: parseInt(match[3], 16)
        }
      : null;
  }
};

export function stripHtml(value) {
  if (!value) return "";
  return value.replace(/<\/?[^>]+(>|$)/g, "");
}
